import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, GridContainer, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { ToggleButton, ToggleButtonGroup, ToggleButtonGroupLayout } from "@amzn/stencil-react-components/toggle-button";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { CreateSpaceModal } from "common/components/Spaces/CreateSpaceModal";
import { SpacePageHeader } from "common/components/Spaces/SpacePageHeader";
import { TeamSpaceCard, TeamSpaceCardData } from "common/components/Spaces/TeamSpaceCard";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

// ── Mock data ──

const MOCK_SPACES: TeamSpaceCardData[] = [
  {
    spaceAlias: "gavindas",
    title: "IT PnD UX",
    description:
      "Team space description placeholder. Team space description placeholder. Team space description placeholder. Team spac..",
    owner: "mbberg",
    toolCount: 18,
    visibility: "Public",
  },
  {
    spaceAlias: "ams-design",
    title: "AMS Design",
    description:
      "Team space description placeholder. Team space description placeholder. Team space description placeholder. Team spac..",
    owner: "yuweilli",
    toolCount: 18,
    visibility: "Public",
  },
  {
    spaceAlias: "engine-marketing",
    title: "Engine marketing team",
    description:
      "Team space description placeholder. Team space description placeholder. Team space description placeholder. Team spac..",
    owner: "yuweilli",
    toolCount: 18,
    visibility: "Public",
  },
  {
    spaceAlias: "it-product-dev",
    title: "IT product and development",
    description:
      "Team space description placeholder. Team space description placeholder. Team space description placeholder. Team spac..",
    owner: "yuweilli",
    toolCount: 18,
    visibility: "Public",
  },
];

const MOCK_CURRENT_USER = "gavindas";
const MAX_SPACES = 5;

type SpaceFilter = "all" | "created-by-me" | "shared-with-me";

const ExploreSpacesPage: FC = () => {
  const history = useHistory();
  const [selectedFilter, setSelectedFilter] = useState<SpaceFilter>("all");

  let hasFeaturePermissions = false;
  try {
    hasFeaturePermissions = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AIFORME_SPACES);
  } catch {
    hasFeaturePermissions = false;
  }

  const userSpaceCount = useMemo(() => MOCK_SPACES.filter((s) => s.owner === MOCK_CURRENT_USER).length, []);
  const canCreateSpace = userSpaceCount < MAX_SPACES;

  const filteredSpaces = useMemo(() => {
    switch (selectedFilter) {
      case "created-by-me":
        return MOCK_SPACES.filter((s) => s.owner === MOCK_CURRENT_USER);
      case "shared-with-me":
        return MOCK_SPACES.filter((s) => s.owner !== MOCK_CURRENT_USER);
      default:
        return MOCK_SPACES;
    }
  }, [selectedFilter]);

  const handleFilterChange = (_event: React.FormEvent<HTMLElement>, value: string | string[]) => {
    setSelectedFilter(value as SpaceFilter);
  };

  if (!hasFeaturePermissions) {
    history.replace("/aiforme");
    return null;
  }

  return (
    <Col>
      <SpacePageHeader
        title={getString("spaces.explore.title")}
        description={getString("spaces.explore.description")}
        breadcrumbs={[
          { title: getString("logoText"), url: "/" },
          { title: "AI for Me", url: "/aiforme" },
          { title: getString("spaces.explore.breadcrumb") },
        ]}
      >
        <Row alignItems="center" gridGap="dimensions.spacing.200">
          <Button variant={ButtonVariant.Primary} onClick={() => history.push("/aiforme/lists")}>
            {getString("spaces.explore.createList")}
          </Button>
          <CreateSpaceModal
            trigger={(open) => (
              <Button
                variant={ButtonVariant.Secondary}
                onClick={canCreateSpace ? open : undefined}
                disabled={!canCreateSpace}
              >
                {getString("spaces.explore.createSpace")}
              </Button>
            )}
          />
        </Row>
        {!canCreateSpace && (
          <Text color="color.text.secondary" fontSize="T100">
            {getString("spaces.explore.maxSpacesReached", { max: String(MAX_SPACES) })}
          </Text>
        )}
      </SpacePageHeader>

      <PageMainContainer>
        <Col gridGap="dimensions.spacing.400" margin={{ top: "dimensions.spacing.500" }}>
          <ToggleButtonGroup
            value={selectedFilter}
            onChange={handleFilterChange}
            layout={ToggleButtonGroupLayout.Horizontal}
          >
            <ToggleButton value="all">{getString("spaces.explore.filterAll")}</ToggleButton>
            <ToggleButton value="created-by-me">{getString("spaces.explore.filterMine")}</ToggleButton>
            <ToggleButton value="shared-with-me">{getString("spaces.explore.filterShared")}</ToggleButton>
          </ToggleButtonGroup>

          <GridContainer>
            {filteredSpaces.map((space) => (
              <TeamSpaceCard
                key={space.spaceAlias}
                space={space}
                onNavigate={(alias) => history.push(`/aiforme/spaces/${alias}`)}
              />
            ))}
          </GridContainer>

          {filteredSpaces.length === 0 && (
            <Col alignItems="center" padding="dimensions.spacing.600">
              <Text color="color.text.secondary">{getString("spaces.explore.noSpaces")}</Text>
            </Col>
          )}
        </Col>
      </PageMainContainer>
    </Col>
  );
};

export default ExploreSpacesPage;
